import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCreateStripePortalSession, useCurrentUser } from 'src/hooks';
import { SubscriptionTier } from 'src/types';
import { cn } from 'src/utils';

type Props = {
  subTier: SubscriptionTier;
};

// Create a comprehensive style map for all subscription-related components
const subStyles = {
  free: {
    card: 'bg-cloudGray',
    plus: 'text-primary',
    baseText: 'text-primary-dark',
    secondaryText: 'text-primary-dark',
  },
  personal: {
    card: 'bg-primary-dark',
    plus: 'text-grassGreen',
    baseText: 'text-offWhite',
    secondaryText: 'text-skyBlue',
  },
  organization: {
    card: 'bg-primary-dark',
    plus: 'text-customYellow',
    baseText: 'text-offWhite',
    secondaryText: 'text-skyBlue',
  },
};

// Helper function to get styles for a specific element based on subscription tier
const getSubStyle = (
  tier: SubscriptionTier,
  element: keyof typeof subStyles.free,
) => {
  return (
    subStyles[tier as keyof typeof subStyles]?.[element] ||
    subStyles.free[element]
  );
};

export const ManageSubCard = ({ subTier }: Props) => {
  const { user } = useCurrentUser();
  const { t } = useTranslation();
  const { mutate: createStripePortalSession } = useCreateStripePortalSession();

  return (
    <div
      className={cn(
        'flex w-full max-w-[800px] flex-col items-center justify-center gap-4 rounded-3xl p-6 sm:flex-row sm:justify-between',
        getSubStyle(subTier, 'card'),
      )}
    >
      <div className='flex w-full max-w-[240px] grow flex-col items-center justify-center gap-4 sm:w-auto sm:max-w-full sm:items-start'>
        <h2
          className={`font-primary text-3xl font-bold leading-[39px] ${getSubStyle(
            subTier,
            'baseText',
          )}`}
        >
          <Trans
            i18nKey={
              subTier === 'free'
                ? t('upgrade.plus_free')
                : subTier === 'personal'
                  ? t('upgrade.plus_personal')
                  : t('upgrade.plus_organization')
            }
            components={[
              <span className={getSubStyle(subTier, 'plus')}></span>,
            ]}
          />
        </h2>

        <div className='flex w-full flex-row flex-nowrap items-center justify-between gap-4 sm:justify-start sm:gap-6 md:gap-8 lg:gap-10'>
          <div className='flex flex-col items-center justify-center gap-2'>
            <h4
              className={`text-100 self-start ${getSubStyle(subTier, 'secondaryText')}`}
            >
              {t('upgrade.cart_history')}
            </h4>
            <span className={`heading-100 ${getSubStyle(subTier, 'baseText')}`}>
              {subTier === 'organization' || subTier === 'personal'
                ? 'All Carts'
                : '5 Latest Carts'}
            </span>
          </div>
          <div className='flex flex-col items-center justify-center gap-2'>
            <h4 className={`text-100 ${getSubStyle(subTier, 'secondaryText')}`}>
              {t('upgrade.users')}
            </h4>
            <span className={`heading-100 ${getSubStyle(subTier, 'baseText')}`}>
              {subTier === 'organization' ? '100' : '1'}
            </span>
          </div>
        </div>
      </div>

      <div className='flex flex-col items-center justify-center gap-4 sm:items-end'>
        <div className='flex flex-col items-center justify-center gap-1 sm:items-end'>
          <span className={`heading-200 ${getSubStyle(subTier, 'baseText')}`}>
            {subTier === 'organization'
              ? '$250'
              : subTier === 'personal'
                ? '$9.99'
                : '$0'}
          </span>
          <span className={`text-100 ${getSubStyle(subTier, 'secondaryText')}`}>
            USD/Month
          </span>
        </div>

        {!user?.subscription.customerId ? null : !user ||
          user?.subscription?.tier === 'free' ||
          user?.subscription?.tier === 'personal' ? (
          <Link to={'/plus/upgrade'}>
            <button className='button-primary h-12'>
              Upgrade Subscription
            </button>
          </Link>
        ) : (
          <button
            className='button-primary h-12'
            onClick={() => createStripePortalSession()}
          >
            Manage Subscription
          </button>
        )}
      </div>
    </div>
  );
};
